/**
 * Viewport: hosts the renderer and owns pointer interaction.
 *
 * Left drag orbits, right/middle drag pans, wheel zooms. While the brush tool is
 * armed, left drag paints the terrain instead — picking uses the renderer's own
 * ray marcher, so the brush lands on exactly the surface that is drawn.
 */

import { useEffect, useRef } from 'react';

import { applyOrbit, applyZoom, frameTerrain } from '../rendering/camera.ts';
import { GpuTerrainRenderer } from '../rendering/gpuTerrain.ts';
import { SoftwareTerrainRenderer } from '../rendering/softwareTerrain.ts';
import { useAnimationLoop, useEvent, type SimulationRuntime } from './runtime.ts';

interface ViewportProps {
  runtime: SimulationRuntime;
  /** Called after each frame with timing figures for the stats panel. */
  onFrameStats: (fps: number, renderMs: number, simMs: number, rasterWidth: number) => void;
  /** While true, left-drag paints instead of orbiting. */
  brushActive: boolean;
}

/**
 * World-box aspect: peak elevation over the width of the grid.
 *
 * The renderer normalises terrain into a unit cube, so this ratio is the only
 * thing that changes the *shape* of what is drawn between presets — and
 * therefore the only input the camera framing needs.
 */
function heightRatio(runtime: SimulationRuntime): number {
  const extent = runtime.params.terrain.resolution * runtime.params.terrain.cellSize;
  return Math.max(0.02, runtime.getMaxHeight() / Math.max(1e-6, extent));
}

export function Viewport({ runtime, onFrameStats, brushActive }: ViewportProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const gpuCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const rendererRef = useRef<SoftwareTerrainRenderer | null>(null);
  const gpuRendererRef = useRef<GpuTerrainRenderer | null>(null);
  const gpuDeviceRef = useRef<GPUDevice | null>(null);
  const dragRef = useRef<{ button: 'rotate' | 'pan'; x: number; y: number } | null>(null);
  const paintingRef = useRef(false);
  const resumeAfterPaintRef = useRef(false);
  const lastPaintUvRef = useRef<[number, number] | null>(null);
  const pointersRef = useRef(new Map<number, { x: number; y: number }>());
  const pinchDistanceRef = useRef<number | null>(null);
  /** Timestamp of the last diagnostics sample, so the grid sweep is throttled. */
  const lastStatsAt = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new SoftwareTerrainRenderer(canvas, runtime.renderWidth);
    rendererRef.current = renderer;
    // Exposed for the headless smoke test, which needs to poke at the renderer
    // to tell "nothing to draw" apart from "drew nothing".
    (window as unknown as { __renderer?: unknown }).__renderer = renderer;

    // Frame the terrain once on mount. Without this the default distance is a
    // guess, and the box is thin enough that a wrong guess puts every ray past
    // the surface.
    const apply = (): void => {
      renderer.resize();
      const rect = canvas.getBoundingClientRect();
      const aspect = rect.height > 0 ? rect.width / rect.height : 1.5;
      runtime.camera = frameTerrain(runtime.camera, heightRatio(runtime), aspect);
      runtime.touch();
    };
    apply();

    // Re-frame when the terrain's shape changes underneath the camera — a new
    // preset or resolution — but not on every statistics update, which would
    // fight the user's own orbiting.
    let lastRatio = heightRatio(runtime);
    const timer = window.setInterval(() => {
      const ratio = heightRatio(runtime);
      if (Math.abs(ratio - lastRatio) > lastRatio * 0.25 + 0.01) {
        lastRatio = ratio;
        apply();
      }
    }, 500);

    const onResize = (): void => renderer.resize();
    window.addEventListener('resize', onResize);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('resize', onResize);
      renderer.destroy();
      gpuRendererRef.current?.destroy();
      gpuRendererRef.current = null;
      gpuDeviceRef.current = null;
      rendererRef.current = null;
    };
  }, [runtime]);

  const onFrame = useEvent((state: Float32Array, width: number, frameMs: number) => {
    const renderer = rendererRef.current;
    const canvas = canvasRef.current;
    if (!renderer || !canvas) return;

    // Read the state from whatever backend is current *now*, not from the value
    // captured when the loop started. The GPU upgrade swaps the backend after the
    // first frames, and a captured array would silently keep pointing at the
    // retired CPU buffer — leaving the simulation running on the GPU while the
    // viewport redrew a frozen world.
    const live = runtime.backend.stateView;
    const gridWidth = runtime.backend.width;

    const options = {
      mode: runtime.mode,
      maxHeight: runtime.getMaxHeight(),
      worldExtent: runtime.params.terrain.resolution * runtime.params.terrain.cellSize,
      showWater: runtime.showWater,
      exposure: runtime.exposure,
      gridStep: runtime.gridStep,
    };
    const gpuView = runtime.backend.getRenderStateView?.();
    let stats;
    if (gpuView?.device && gpuView.stateBuffer && gpuCanvasRef.current) {
      if (!gpuRendererRef.current || gpuDeviceRef.current !== gpuView.device) {
        gpuRendererRef.current?.destroy();
        gpuRendererRef.current = new GpuTerrainRenderer(gpuCanvasRef.current, gpuView.device);
        gpuDeviceRef.current = gpuView.device;
      }
      canvas.style.opacity = '0';
      gpuCanvasRef.current.style.opacity = '1';
      stats = gpuRendererRef.current.render(gpuView, runtime.camera, options);
    } else {
      canvas.style.opacity = '1';
      if (gpuCanvasRef.current) gpuCanvasRef.current.style.opacity = '0';
      stats = renderer.render(live, gridWidth, runtime.camera, options);
    }
    void state;
    void width;
    void frameMs;

    onFrameStats(runtime.fps, stats.renderMs, runtime.getStats().simTimeMs, renderer.width);

    // Diagnostics are a full sweep of the grid (several reductions plus a peak
    // search), so they are sampled rather than computed every frame. About 1.7
    // times a second keeps the Info panel live without spending a frame on it.
    const now = performance.now();
    if (now - lastStatsAt.current > 600) {
      lastStatsAt.current = now;
      if (runtime.backend.kind === 'gpu') {
        const gpu = runtime.backend as { syncFromGpu?: () => Promise<void> };
        void gpu.syncFromGpu?.().then(() => {
          // Keep the CPU height hierarchy current for accurate brush picking;
          // unlike the former renderer path this performs no pixel tracing.
          renderer.preparePicking(runtime.backend.stateView, runtime.backend.width, options);
          const s = runtime.backend.stats();
          runtime.setStats({ ...s, renderTimeMs: stats.renderMs });
        });
      } else {
        const s = runtime.backend.stats();
        runtime.setStats({ ...s, renderTimeMs: stats.renderMs });
      }
    }
  });

  useAnimationLoop({ runtime, onFrame });

  // The raster width is applied live, so the select in the Info panel takes effect
  // immediately instead of on the next reload.
  useEffect(() => {
    const renderer = rendererRef.current;
    if (!renderer) return;
    renderer.setInternalWidth(runtime.renderWidth);
  }, [runtime, runtime.renderWidth]);

  /* ---- interaction ------------------------------------------------------ */

  const paintAt = (offsetX: number, offsetY: number): void => {
    const renderer = rendererRef.current;
    const canvas = canvasRef.current;
    if (!renderer || !canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ndcX = (2 * offsetX) / Math.max(1, rect.width) - 1;
    const ndcY = 1 - (2 * offsetY) / Math.max(1, rect.height);
    const hit = renderer.pick(
      ndcX,
      ndcY,
      runtime.camera,
      runtime.backend.width,
      runtime.getMaxHeight(),
    );
    if (!hit) return;
    const settings = runtime.brush;
    const u = hit[0];
    const v = hit[2];
    const previous = lastPaintUvRef.current;
    const distance = previous ? Math.hypot(u - previous[0], v - previous[1]) : 0;
    // Pointer devices may emit repeated move events at the same coordinates.
    // Ignoring them keeps a stationary stroke independent of event rate.
    if (previous && distance < 1e-6) return;
    const spacing = Math.max(0.002, settings.radius * 0.35);
    const stamps = previous ? Math.min(64, Math.max(1, Math.ceil(distance / spacing))) : 1;
    // A short segment contributes proportionally less than a full stamp. This
    // makes the accumulated strength depend on travelled distance rather than
    // how often the browser happened to deliver pointer events.
    const segmentStrength = previous
      ? settings.strength * Math.min(1, distance / (stamps * spacing))
      : settings.strength;
    for (let n = 1; n <= stamps; n++) {
      const t = n / stamps;
      runtime.applyBrush({
        u: previous ? previous[0] + (u - previous[0]) * t : u,
        v: previous ? previous[1] + (v - previous[1]) * t : v,
        radius: settings.radius,
        strength: segmentStrength,
        mode: settings.mode,
        active: true,
      });
    }
    lastPaintUvRef.current = [u, v];
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size >= 2) {
      if (paintingRef.current) finishPainting();
      dragRef.current = null;
      const [a, b] = Array.from(pointersRef.current.values());
      pinchDistanceRef.current = Math.hypot(a.x - b.x, a.y - b.y);
      return;
    }
    if (brushActive && e.button === 0) {
      resumeAfterPaintRef.current = runtime.running;
      if (runtime.running) runtime.setRunning(false);
      paintingRef.current = true;
      lastPaintUvRef.current = null;
      paintAt(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
      return;
    }
    dragRef.current = { button: e.button === 0 ? 'rotate' : 'pan', x: e.clientX, y: e.clientY };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    if (pointersRef.current.has(e.pointerId)) {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }
    if (pointersRef.current.size >= 2) {
      if (paintingRef.current) finishPainting();
      dragRef.current = null;
      const [a, b] = Array.from(pointersRef.current.values());
      const distance = Math.max(1, Math.hypot(a.x - b.x, a.y - b.y));
      const previous = pinchDistanceRef.current;
      if (previous && previous > 0) {
        // Spreading two fingers zooms in. Convert the distance ratio into the
        // same logarithmic delta used by wheel zoom, so pinch scale is smooth
        // and independent of event frequency.
        const deltaY = -Math.log(distance / previous) / 0.0012;
        runtime.camera = applyZoom(runtime.camera, deltaY);
        runtime.touch();
      }
      pinchDistanceRef.current = distance;
      return;
    }
    if (paintingRef.current) {
      paintAt(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
      return;
    }
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.x;
    const dy = e.clientY - drag.y;
    drag.x = e.clientX;
    drag.y = e.clientY;
    const rect = e.currentTarget.getBoundingClientRect();
    runtime.camera = applyOrbit(runtime.camera, dx, dy, drag.button, rect.height);
    runtime.touch();
  };

  function finishPainting(): void {
    if (!paintingRef.current) return;
    paintingRef.current = false;
    lastPaintUvRef.current = null;
    runtime.backend.endStroke();
    if (resumeAfterPaintRef.current) runtime.setRunning(true);
    resumeAfterPaintRef.current = false;
  }

  const endDrag = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchDistanceRef.current = null;
    dragRef.current = null;
    finishPainting();
    // If one finger remains after a pinch, let it continue as a fresh orbit
    // without jumping from coordinates recorded before the pinch.
    const remaining = pointersRef.current.values().next().value as { x: number; y: number } | undefined;
    if (remaining) {
      dragRef.current = { button: 'rotate', x: remaining.x, y: remaining.y };
    }
  };

  return (
    <div className="relative h-full w-full">
      <canvas ref={gpuCanvasRef} className="pointer-events-none absolute inset-0 block h-full w-full opacity-0" />
      <canvas
      ref={canvasRef}
      className={`absolute inset-0 block h-full w-full touch-none transition-none ${
        brushActive ? 'cursor-crosshair' : 'cursor-grab active:cursor-grabbing'
      }`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onWheel={(e) => {
        runtime.camera = applyZoom(runtime.camera, e.deltaY);
        runtime.touch();
      }}
      onContextMenu={(e) => e.preventDefault()}
      />
    </div>
  );
}
